import { Badge, Box, Button, Flex, Heading, HStack, Modal, Text, Textarea, VStack } from 'components/ui';
import { useTranslation } from 'hooks/useTranslation';
import { useCallback, useState } from 'react';
import { MdClose, MdContentCopy, MdSearch } from 'react-icons/md';
import { knowledgeBaseService } from 'services/knowledgeBaseService';
import { KbRetrievedChunkDto } from 'types/knowledgeBase';

interface TestRetrievalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TestRetrievalModal = ({ isOpen, onClose }: TestRetrievalModalProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<KbRetrievedChunkDto[] | null>(null);

  const resetState = useCallback(() => {
    setQuery('');
    setError(null);
    setResults(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleTest = async () => {
    if (!query.trim()) {
      setError(t('app.knowledgeBase.testRetrieval.queryRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResults(null);

      const response = await knowledgeBaseService.testRetrieval({
        query: query.trim(),
        topK: 3
      });

      if (!response.success) {
        setError(response.errorMessage || t('app.knowledgeBase.testRetrieval.failed'));
        return;
      }

      setResults(response.chunks);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('app.knowledgeBase.testRetrieval.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleTest();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatScore = (score: number) => {
    return (score * 100).toFixed(1) + '%';
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <Heading size="lg" color={{ base: 'navy.700', _dark: 'white' }}>
            {t('app.knowledgeBase.testRetrieval.title')}
          </Heading>
          <Modal.CloseTrigger asChild>
            <Button variant="ghost" size="sm" p={0}>
              <MdClose className="h-6 w-6" />
            </Button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>

      <Modal.Body>
        <VStack gap={4}>
          {/* Error message */}
          {error && (
            <Flex w="full" p={3} borderRadius="lg" bg={{ base: 'red.100', _dark: 'red.900' }} color={{ base: 'red.700', _dark: 'red.100' }}>
              <Text fontSize="sm">{error}</Text>
            </Flex>
          )}

          {/* Query input */}
          <Box w="full">
            <Text fontSize="sm" fontWeight="medium" mb={2} color={{ base: 'navy.700', _dark: 'white' }}>
              {t('app.knowledgeBase.testRetrieval.queryLabel')}
            </Text>
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={t('app.knowledgeBase.testRetrieval.queryPlaceholder')}
              rows={3}
              disabled={loading}
            />
            <Text fontSize="xs" color={{ base: 'gray.500', _dark: 'gray.400' }} mt={1}>
              {t('app.knowledgeBase.testRetrieval.queryHint')}
            </Text>
          </Box>

          {/* Results */}
          {results !== null && (
            <Box w="full">
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" fontWeight="medium" color={{ base: 'navy.700', _dark: 'white' }}>
                  {t('app.knowledgeBase.testRetrieval.resultsLabel')}
                </Text>
                <Badge colorPalette={results.length > 0 ? 'green' : 'gray'}>
                  {results.length} {t('app.knowledgeBase.testRetrieval.chunksFound')}
                </Badge>
              </HStack>

              {results.length === 0 ? (
                <Flex
                  w="full"
                  p={4}
                  borderRadius="lg"
                  bg={{ base: 'gray.50', _dark: 'gray.800' }}
                  justify="center"
                >
                  <Text fontSize="sm" color={{ base: 'gray.600', _dark: 'gray.400' }}>
                    {t('app.knowledgeBase.testRetrieval.noResults')}
                  </Text>
                </Flex>
              ) : (
                <VStack gap={3} w="full" maxH="400px" overflowY="auto">
                  {results.map((chunk, index) => (
                    <Box
                      key={index}
                      w="full"
                      p={3}
                      borderRadius="lg"
                      border="1px solid"
                      borderColor={{ base: 'gray.200', _dark: 'gray.700' }}
                      bg={{ base: 'white', _dark: 'gray.800' }}
                    >
                      <HStack justify="space-between" mb={2}>
                        <HStack gap={2}>
                          <Badge colorPalette="blue" variant="subtle">
                            #{index + 1}
                          </Badge>
                          <Badge colorPalette="green" variant="outline">
                            {t('app.knowledgeBase.testRetrieval.score')}: {formatScore(chunk.score)}
                          </Badge>
                        </HStack>
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => copyToClipboard(chunk.content)}
                          title={t('app.common.copy')}
                        >
                          <MdContentCopy />
                        </Button>
                      </HStack>

                      {chunk.sourceFileName && (
                        <Text fontSize="xs" color={{ base: 'gray.500', _dark: 'gray.400' }} mb={2}>
                          {t('app.knowledgeBase.testRetrieval.source')}: {chunk.sourceFileName}
                        </Text>
                      )}

                      <Box
                        p={2}
                        borderRadius="md"
                        bg={{ base: 'gray.50', _dark: 'gray.900' }}
                        maxH="150px"
                        overflowY="auto"
                      >
                        <Text fontSize="sm" whiteSpace="pre-wrap" color={{ base: 'gray.700', _dark: 'gray.300' }}>
                          {chunk.content}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          )}
        </VStack>
      </Modal.Body>

      <Modal.Footer>
        <HStack gap={3} w="full">
          <Button
            variant="subtle"
            colorPalette="gray"
            onClick={handleClose}
            flex={1}
          >
            {t('app.common.cancel')}
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleTest}
            disabled={!query.trim() || loading}
            loading={loading}
            flex={1}
          >
            <MdSearch />
            {t('app.knowledgeBase.testRetrieval.testButton')}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default TestRetrievalModal;
